<!--
 * @Description: 线索转化
 * @Author: mdb
 * @Date: 2023-04-11 14:58:40
 * @LastEditTime: 2023-04-21 14:56:53
 * @LastEditors: mdb
-->
<template>
  <div class="home-echarts-wrap" :style="{ width, height }">
    <div class="home-echarts-header">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, inject } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import type { Ref, PropType } from 'vue'
interface ItemType {
  clueType: number
  clueTypeStr: string
  num: number
}
interface ValueProps {
  transVos: ItemType[]
  [key: string]: any
}
enum Subdomain {
  Fbu = 'fbu',
  Lbu = 'lbu'
}
const subdomain = inject<Subdomain>('subdomain') || Subdomain.Fbu

const props = defineProps({
  title: {
    type: String as PropType<string>,
    required: true
  },
  value: {
    type: Object as PropType<ValueProps>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '437px'
  },
  spinning: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  }
})

const fbuEnum = [
  { value: 100, name: '全部线索', itemStyle: { color: '#3F97FF' } },
  { value: 86, name: '有效线索', itemStyle: { color: '#66ACFF' } },
  { value: 71, name: '投放线索', itemStyle: { color: '#8CC1FF' } },
  { value: 55, name: '潜在客户', itemStyle: { color: '#B2D6FF' } },
  { value: 38, name: '签约客户', itemStyle: { color: '#B2D6FF' } },
  { value: 0, name: '成交客户', itemStyle: { color: '#D9EAFF' } }
]
const lbuEnum = [
  { value: 100, name: '全部线索', itemStyle: { color: '#3F97FF' } },
  { value: 80, name: '有效线索', itemStyle: { color: '#66ACFF' } },
  { value: 60, name: '投放线索', itemStyle: { color: '#8CC1FF' } },
  { value: 40, name: '潜在客户', itemStyle: { color: '#B2D6FF' } },
  { value: 0, name: '成交客户', itemStyle: { color: '#D9EAFF' } }
]
const funnelEnum = {
  [Subdomain.Fbu]: fbuEnum,
  [Subdomain.Lbu]: lbuEnum
}

const showECharts = ref(false)
const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>)
const initChart = (data: ItemType[]) => {
  const labelLine = data.map((item) => ({
    value: 0,
    name: item.clueTypeStr,
    itemStyle: { color: '#999' }
  }))
  const funnelList = data.map((item, index) => ({
    value: funnelEnum[subdomain][index].value,
    name: item.clueTypeStr,
    itemStyle: funnelEnum[subdomain][index].itemStyle
  }))
  setOptions({
    tooltip: {
      trigger: 'item',
      formatter: '{b} : {c}'
    },
    series: [
      {
        type: 'funnel',
        left: 455,
        top: 30,
        bottom: 60,
        width: 0,
        min: 0,
        max: 0,
        minSize: 0,
        maxSize: 0,
        sort: 'none',
        z: 1,
        gap: 2,
        label: {
          show: true,
          position: 'left',
          formatter: (params) => {
            return params.name
          }
        },
        labelLine: {
          length: 300,
          lineStyle: {
            width: 1,
            type: 'dashed',
            color: '#ccc'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: () => ''
        },
        data: labelLine
      },
      {
        type: 'funnel',
        left: 150,
        top: 30,
        bottom: 60,
        width: '70%',
        min: 20,
        max: 100,
        minSize: '10%',
        maxSize: '100%',
        sort: 'none',
        gap: 2,
        label: { show: false },
        emphasis: {
          label: { show: false }
        },
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const { name } = params
            const item = data.find((item) => item.clueTypeStr === name)
            return `${name} : ${item?.num}` || ''
          }
        },
        data: funnelList
      }
    ]
  })
}

watch(
  () => props.value,
  (newValue) => {
    if (newValue && newValue.transVos && newValue.transVos.length > 0) {
      showECharts.value = true
      setTimeout(() => initChart(newValue.transVos))
    } else {
      showECharts.value = false
      setTimeout(() => initChart([]))
    }
  },
  {
    deep: true,
    immediate: true
  }
)
</script>
<script lang="ts">
export default { name: 'LeadConversion' }
</script>

<style scoped></style>
